import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Timeout configurations (in milliseconds)
const TIMEOUTS = {
  DEFAULT: 30000,        // 30 seconds for most operations
  QUICK: 10000,          // 10 seconds for quick operations like status checks
  LONG: 120000,          // 2 minutes for heavy operations
  VERY_LONG: 300000      // 5 minutes for very heavy operations like cluster installation
};

class ApiService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
      timeout: TIMEOUTS.DEFAULT
    });
  }

  // Helper to make requests with custom timeout
  async requestWithTimeout(config, timeout) {
    return this.client.request({ ...config, timeout });
  }

  // Auth
  async login(username, password) {
    const response = await this.client.post('/auth/login', { username, password });
    return response.data;
  }

  async logout() {
    const response = await this.client.post('/auth/logout');
    return response.data;
  }

  async changePassword(username, oldPassword, newPassword) {
    const response = await this.client.post('/auth/change-password', { username, oldPassword, newPassword });
    return response.data;
  }

  async getUsers() {
    const response = await this.client.get('/auth/users');
    return response.data;
  }

  // Installation
  async createInstallationJob(data) {
    const response = await this.client.post('/installation/deploy', data);
    return response.data;
  }

  async createUninstallJob(data) {
    const response = await this.client.post('/uninstall', data);
    return response.data;
  }

  async getJobStatus(jobId) {
    const response = await this.client.get(`/installation/job/${jobId}`);
    return response.data;
  }

  async getAllJobs() {
    const response = await this.client.get('/installation/jobs');
    return response.data;
  }

  async removeJob(jobId) {
    const response = await this.client.delete(`/installation/job/${jobId}`);
    return response.data;
  }

  // Cancel a running job and optionally run cleanup
  async cancelJob(jobId, runCleanup = true) {
    const response = await this.client.post(`/installation/job/${jobId}/cancel`, { runCleanup });
    return response.data;
  }

  async getKafkaVersions() {
    const response = await this.client.get('/installation/kafka-versions');
    return response.data;
  }

  // SSH Keys
  async getSSHKeys() {
    const response = await this.client.get('/sshkeys');
    return response.data;
  }

  async getSSHKey(id) {
    const response = await this.client.get(`/sshkeys/${id}`);
    return response.data;
  }

  async createSSHKey(data) {
    const response = await this.client.post('/sshkeys', data);
    return response.data;
  }

  async updateSSHKey(id, data) {
    const response = await this.client.put(`/sshkeys/${id}`, data);
    return response.data;
  }

  async deleteSSHKey(id) {
    const response = await this.client.delete(`/sshkeys/${id}`);
    return response.data;
  }

  // Clusters
  async getClusters() {
    const response = await this.client.get('/clusters');
    return response.data;
  }

  async saveCluster(data) {
    const response = await this.client.post('/clusters', data);
    return response.data;
  }

  async getCluster(id) {
    const response = await this.client.get(`/clusters/${id}`);
    return response.data;
  }

  async updateCluster(id, data) {
    const response = await this.client.put(`/clusters/${id}`, data);
    return response.data;
  }

  async deleteCluster(id) {
    const response = await this.client.delete(`/clusters/${id}`);
    return response.data;
  }

  async getClusterStatus(id, credentials) {
    const response = await this.client.post(`/clusters/${id}/status`, { credentials });
    return response.data;
  }

  async nodeAction(clusterId, nodeHost, action, credentials) {
    const response = await this.client.post(`/clusters/${clusterId}/node/${nodeHost}/action`, { action, credentials });
    return response.data;
  }

  async checkClusterConnection(clusterId) {
    const response = await this.client.post(`/clusters/${clusterId}/check`);
    return response.data;
  }

  async testNetworkConnectivity(clusterId) {
    const response = await this.client.post(`/clusters/${clusterId}/test-network`);
    return response.data;
  }

  async getClusterInfo(clusterId) {
    const response = await this.client.post(`/clusters/${clusterId}/info`);
    return response.data;
  }

  async getClusterHealth(clusterId) {
    const response = await this.client.post(`/clusters/${clusterId}/health`);
    return response.data;
  }

  async renameCluster(clusterId, name) {
    const response = await this.client.put(`/clusters/${clusterId}/rename`, { name });
    return response.data;
  }

  // Topics
  async getTopics(clusterId) {
    const response = await this.client.post(`/clusters/${clusterId}/topics`);
    return response.data;
  }

  async createTopic(clusterId, topicName, partitions, replicationFactor) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/create`, { topicName, partitions, replicationFactor });
    return response.data;
  }

  async describeTopic(clusterId, topicName) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/${topicName}/describe`);
    return response.data;
  }

  async deleteTopic(clusterId, topicName) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/${topicName}/delete`);
    return response.data;
  }

  async updateTopicConfig(clusterId, topicName, config) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/${topicName}/config`, { config });
    return response.data;
  }

  async addTopicPartitions(clusterId, topicName, totalPartitions) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/${topicName}/partitions`, { totalPartitions });
    return response.data;
  }

  // Consumer Groups
  async getConsumerGroups(clusterId) {
    const response = await this.client.post(`/clusters/${clusterId}/consumer-groups`);
    return response.data;
  }

  async describeConsumerGroup(clusterId, groupId) {
    const response = await this.client.post(`/clusters/${clusterId}/consumer-groups/${groupId}/describe`);
    return response.data;
  }

  async deleteConsumerGroup(clusterId, groupId) {
    const response = await this.client.post(`/clusters/${clusterId}/consumer-groups/${groupId}/delete`);
    return response.data;
  }

  async resetConsumerGroupOffsets(clusterId, groupId, topicName, offset) {
    const response = await this.client.post(`/clusters/${clusterId}/consumer-groups/${groupId}/reset-offsets`, { topicName, offset });
    return response.data;
  }

  async getTopicConsumerGroups(clusterId, topicName) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/${topicName}/consumer-groups`);
    return response.data;
  }

  // Messages - Paginated
  async getMessages(clusterId, topicName, pageSize = 10, page = 1, latest = false) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/${topicName}/messages`, { pageSize, page, latest });
    return response.data;
  }

  async produceMessage(clusterId, topicName, key, value, headers = {}) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/${topicName}/produce`, { key, value, headers });
    return response.data;
  }

  async startMessageStream(clusterId, topicName) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/${topicName}/stream/start`);
    return response.data;
  }

  async stopMessageStream(clusterId, topicName) {
    const response = await this.client.post(`/clusters/${clusterId}/topics/${topicName}/stream/stop`);
    return response.data;
  }

  // SQL
  async executeSql(clusterId, query) {
    const response = await this.client.post(`/clusters/${clusterId}/sql`, { query });
    return response.data;
  }

  // SSL
  async getSSLInfo(clusterId) {
    const response = await this.client.get(`/clusters/${clusterId}/ssl/info`);
    return response.data;
  }

  async downloadSSLCerts(clusterId, credentials) {
    const response = await this.client.post(`/clusters/${clusterId}/ssl/download`, { credentials });
    return response.data;
  }

  // Uninstall
  async uninstallCluster(clusterId, credentials, confirmed) {
    const response = await this.client.post(`/clusters/${clusterId}/uninstall`, { credentials, confirmed });
    return response.data;
  }

  // SSH Terminal
  async sshConnect(clusterId, nodeHost, credentials) {
    const response = await this.client.post(`/clusters/${clusterId}/ssh/${nodeHost}/connect`, { credentials });
    return response.data;
  }

  async sshExecute(clusterId, nodeHost, command, credentials) {
    const response = await this.client.post(`/clusters/${clusterId}/ssh/${nodeHost}/execute`, { command, credentials });
    return response.data;
  }

  // Node Stats (CPU, RAM, DISK)
  async getNodeStats(clusterId, credentials) {
    const response = await this.client.post(`/clusters/${clusterId}/node-stats`, { credentials });
    return response.data;
  }

  // ============================================
  // ksqlDB API
  // ============================================

  async getKsqlDBInfo(clusterId) {
    const response = await this.client.get(`/clusters/${clusterId}/ksqldb`);
    return response.data;
  }

  async installKsqlDB(clusterId, data) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/install`, data);
    return response.data;
  }

  async uninstallKsqlDB(clusterId, credentials, instanceName = null) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/uninstall`, { 
      credentials, 
      instanceName 
    });
    return response.data;
  }

  async getKsqlDBStatus(clusterId, credentials, instanceName = null) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/status`, { 
      credentials,
      instanceName 
    });
    return response.data;
  }

  async ksqlDBAction(clusterId, action, credentials, instanceName = null) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/action`, { 
      action, 
      credentials,
      instanceName 
    });
    return response.data;
  }

  async setKsqlDBPrimary(clusterId, instanceName) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/set-primary`, { instanceName });
    return response.data;
  }

  async updateKsqlDBConfig(clusterId, credentials, instanceName = null) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/update-config`, { 
      credentials,
      instanceName 
    });
    return response.data;
  }

  async ksqlDBQuery(clusterId, ksql, streamsProperties = {}) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/query`, { ksql, streamsProperties });
    return response.data;
  }

  // Push query with timeout (for EMIT CHANGES with LIMIT or time-bounded)
  async ksqlDBPushQuery(clusterId, ksql, streamsProperties = {}, timeout = 30000) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/push-query`, { 
      ksql, 
      streamsProperties,
      timeout 
    }, { timeout: timeout + 5000 }); // Client timeout slightly longer
    return response.data;
  }

  // Get EventSource URL for streaming queries
  getKsqlDBStreamUrl(clusterId) {
    return `${API_BASE_URL}/clusters/${clusterId}/ksqldb/query-stream`;
  }

  async getKsqlDBStreams(clusterId) {
    const response = await this.client.get(`/clusters/${clusterId}/ksqldb/streams`);
    return response.data;
  }

  async getKsqlDBTables(clusterId) {
    const response = await this.client.get(`/clusters/${clusterId}/ksqldb/tables`);
    return response.data;
  }

  async getKsqlDBQueries(clusterId) {
    const response = await this.client.get(`/clusters/${clusterId}/ksqldb/queries`);
    return response.data;
  }

  async getKsqlDBServerInfo(clusterId) {
    const response = await this.client.get(`/clusters/${clusterId}/ksqldb/info`);
    return response.data;
  }

  async terminateKsqlDBQuery(clusterId, queryId) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/terminate/${queryId}`);
    return response.data;
  }

  async describeKsqlDBObject(clusterId, name, extended = false) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/describe`, { name, extended });
    return response.data;
  }

  async dropKsqlDBObject(clusterId, type, name, deleteTopic = false) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/drop`, { type, name, deleteTopic });
    return response.data;
  }

  async getKsqlDBLogs(clusterId, credentials, lines = 100) {
    const response = await this.client.post(`/clusters/${clusterId}/ksqldb/logs`, { credentials, lines });
    return response.data;
  }

  // ============================================
  // ACL Management API
  // ============================================

  // ============================================
  // ACL Configuration API (Enable/Disable)
  // ============================================

  async getACLStatus(clusterId, credentials) {
    const response = await this.client.post(`/clusters/${clusterId}/acl-config/status`, { credentials });
    return response.data;
  }

  async enableACL(clusterId, credentials, options = {}) {
    const { superUsers = [], allowEveryoneIfNoAcl = true, restartKafka = false } = options;
    // Use LONG timeout for ACL operations (may include restart)
    const response = await this.client.post(`/clusters/${clusterId}/acl-config/enable`, { 
      credentials, 
      superUsers, 
      allowEveryoneIfNoAcl, 
      restartKafka 
    }, { timeout: TIMEOUTS.LONG });
    return response.data;
  }

  async disableACL(clusterId, credentials, restartKafka = false) {
    // Use LONG timeout for ACL operations (may include restart)
    const response = await this.client.post(`/clusters/${clusterId}/acl-config/disable`, { 
      credentials, 
      restartKafka 
    }, { timeout: TIMEOUTS.LONG });
    return response.data;
  }

  // ============================================
  // ACL Management API
  // ============================================

  async listACLs(clusterId, credentials, resourceType = null, resource = null) {
    if (resourceType || resource) {
      const response = await this.client.post(`/clusters/${clusterId}/acls/list`, { credentials, resourceType, resource });
      return response.data;
    }
    const response = await this.client.get(`/clusters/${clusterId}/acls`, { 
      params: { credentials: JSON.stringify(credentials) } 
    });
    return response.data;
  }

  async addACL(clusterId, credentials, aclConfig) {
    const response = await this.client.post(`/clusters/${clusterId}/acls/add`, { credentials, ...aclConfig });
    return response.data;
  }

  async removeACL(clusterId, credentials, aclConfig) {
    const response = await this.client.post(`/clusters/${clusterId}/acls/remove`, { credentials, ...aclConfig });
    return response.data;
  }

  // ============================================
  // User Management API (SCRAM)
  // ============================================

  async listUsers(clusterId, credentials) {
    const response = await this.client.post(`/clusters/${clusterId}/users/list`, { credentials });
    return response.data;
  }

  async createUser(clusterId, credentials, username, password, mechanism = 'SCRAM-SHA-512') {
    const response = await this.client.post(`/clusters/${clusterId}/users/create`, { 
      credentials, username, password, mechanism 
    });
    return response.data;
  }

  async deleteUser(clusterId, credentials, username) {
    const response = await this.client.post(`/clusters/${clusterId}/users/delete`, { credentials, username });
    return response.data;
  }

  async describeUser(clusterId, credentials, username) {
    const response = await this.client.post(`/clusters/${clusterId}/users/describe`, { credentials, username });
    return response.data;
  }

  // ============================================
  // Quota Management API
  // ============================================

  async listQuotas(clusterId, credentials) {
    const response = await this.client.post(`/clusters/${clusterId}/quotas/list`, { credentials });
    return response.data;
  }

  async setQuota(clusterId, credentials, quotaConfig) {
    const response = await this.client.post(`/clusters/${clusterId}/quotas/set`, { credentials, ...quotaConfig });
    return response.data;
  }

  async deleteQuota(clusterId, credentials, username, clientId) {
    const response = await this.client.post(`/clusters/${clusterId}/quotas/delete`, { credentials, username, clientId });
    return response.data;
  }

  async describeQuota(clusterId, credentials, username, clientId) {
    const response = await this.client.post(`/clusters/${clusterId}/quotas/describe`, { credentials, username, clientId });
    return response.data;
  }

  // ============================================
  // Production Security API
  // ============================================

  async getSecurityStatus(clusterId) {
    const response = await this.client.get(`/clusters/${clusterId}/security/status`);
    return response.data;
  }

  async setupProductionSecurity(clusterId, credentials, options) {
    // Use VERY_LONG timeout for production security setup (can take several minutes)
    const response = await this.client.post(`/clusters/${clusterId}/security/setup`, {
      credentials,
      ...options
    }, { timeout: TIMEOUTS.VERY_LONG });
    return response.data;
  }

  async updateSecurityCredentials(clusterId, credentialsConfig) {
    const response = await this.client.post(`/clusters/${clusterId}/security/credentials`, credentialsConfig);
    return response.data;
  }

  async removeSecurityCredentials(clusterId) {
    const response = await this.client.delete(`/clusters/${clusterId}/security/credentials`);
    return response.data;
  }

  // ============================================
  // Cluster Node Management
  // ============================================

  async addNodesToCluster(clusterId, data) {
    // Use VERY_LONG timeout as adding nodes can take several minutes
    const response = await this.client.post(`/clusters/${clusterId}/add-nodes/sync`, data, {
      timeout: TIMEOUTS.VERY_LONG
    });
    return response.data;
  }

  // Health
  async healthCheck() {
    const response = await this.client.get('/health', { baseURL: API_BASE_URL.replace('/api', '') });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
